/**
 * FormInput Component
 * Reusable form input with label and consistent styling
 *
 * @deprecated Use TextField from '@/components/base' instead.
 * TextField has more features: icons, password toggle, size variants.
 *
 * Migration example:
 * // Before:
 * <FormInput label="Email" type="email" value={email} onChange={handleChange} />
 *
 * // After:
 * <TextField label="Email" type="email" value={email} onChange={handleChange} />
 */

import { forwardRef, InputHTMLAttributes, TextareaHTMLAttributes } from 'react';

interface BaseProps {
    label: string;
    error?: string;
    helperText?: string;
}

interface InputProps extends BaseProps, InputHTMLAttributes<HTMLInputElement> {
    as?: 'input';
}

interface TextareaProps extends BaseProps, TextareaHTMLAttributes<HTMLTextAreaElement> {
    as: 'textarea';
    rows?: number;
}

type FormInputProps = InputProps | TextareaProps;

/**
 * FormInput - A reusable form input component
 *
 * @example
 * // Basic input
 * <FormInput label="Email" type="email" value={email} onChange={handleChange} />
 *
 * // Textarea
 * <FormInput as="textarea" label="Bio" rows={4} value={bio} onChange={handleChange} />
 *
 * // With error
 * <FormInput label="Name" error="Name is required" value={name} onChange={handleChange} />
 */
export const FormInput = forwardRef<HTMLInputElement | HTMLTextAreaElement, FormInputProps>(
    (props, ref) => {
        const { label, error, helperText, className = '', ...rest } = props;

        const baseClasses = `
            w-full px-4 py-2 border rounded-lg transition-colors
            focus:ring-2 focus:ring-primary focus:border-transparent
            disabled:bg-gray-100 disabled:cursor-not-allowed
            ${error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'}
            ${className}
        `.trim().replace(/\s+/g, ' ');

        const isTextarea = props.as === 'textarea';

        return (
            <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                    {rest.required && <span className="text-red-500 ml-1">*</span>}
                </label>

                {isTextarea ? (
                    <textarea
                        ref={ref as React.Ref<HTMLTextAreaElement>}
                        className={`${baseClasses} resize-none`}
                        rows={(rest as TextareaProps).rows || 4}
                        {...(rest as TextareaHTMLAttributes<HTMLTextAreaElement>)}
                    />
                ) : (
                    <input
                        ref={ref as React.Ref<HTMLInputElement>}
                        className={baseClasses}
                        {...(rest as InputHTMLAttributes<HTMLInputElement>)}
                    />
                )}

                {error && (
                    <p className="mt-1 text-sm text-red-500">{error}</p>
                )}

                {helperText && !error && (
                    <p className="mt-1 text-sm text-gray-500">{helperText}</p>
                )}
            </div>
        );
    }
);

FormInput.displayName = 'FormInput';

export default FormInput;
